/**
 * Dashboard view component.
 * @module components/View/DashboardView
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Label } from 'semantic-ui-react';
import { Link } from 'react-router-dom';

import { Icon } from '@plone/volto/components';
import { getContent, getVocabulary } from '@plone/volto/actions';
import { useLazyLibs } from '@plone/volto/helpers/Loadable/Loadable';
import {
  customSelectStyles,
  DropdownIndicator,
  ClearIndicator,
  selectTheme,
} from '@plone/volto/components/manage/Widgets/SelectStyling';

import leftKeySVG from '@plone/volto/icons/left-key.svg';
import rightKeySVG from '@plone/volto/icons/right-key.svg';
import upSVG from '@plone/volto/icons/up-key.svg';
import downSVG from '@plone/volto/icons/down-key.svg';

const periodOptions = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
];

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const startOfWeek = (date) => {
  const start = new Date(date);
  // getDay(): 0 = Sunday .. 6 = Saturday. Shift so Monday is the start.
  const daysSinceMonday = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - daysSinceMonday);
  start.setHours(0, 0, 0, 0);
  return start;
};

const endOfWeek = (date) => {
  const end = startOfWeek(date);
  end.setDate(end.getDate() + 6);
  return end;
};

// ISO 8601 week number (weeks start on Monday, week 1 is the week
// containing the year's first Thursday).
const getWeekNumber = (date) => {
  const target = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  const dayNumber = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - dayNumber);
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  return Math.ceil(((target - yearStart) / 86400000 + 1) / 7);
};

const shiftDate = (date, period, amount) => {
  const shifted = new Date(date);
  if (period === 'day') shifted.setDate(shifted.getDate() + amount);
  else if (period === 'week') shifted.setDate(shifted.getDate() + amount * 7);
  else if (period === 'month') shifted.setMonth(shifted.getMonth() + amount);
  else if (period === 'year')
    shifted.setFullYear(shifted.getFullYear() + amount);
  return shifted;
};

const formatPeriodLabel = (date, period) => {
  if (period === 'day') {
    return `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  }
  if (period === 'week') {
    const start = startOfWeek(date);
    const end = endOfWeek(date);
    const startLabel = `${monthNames[start.getMonth()].slice(0, 3)} ${start.getDate()}`;
    const endLabel =
      start.getMonth() === end.getMonth()
        ? `${end.getDate()}`
        : `${monthNames[end.getMonth()].slice(0, 3)} ${end.getDate()}`;
    return `${startLabel} - ${endLabel}, ${end.getFullYear()}`;
  }
  if (period === 'year') {
    return `${date.getFullYear()}`;
  }
  return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
};

// Start/end datetime bounds of the currently selected day/week/month/year.
const getPeriodRange = (date, period) => {
  let start;
  let end;
  if (period === 'day') {
    start = new Date(date);
    start.setHours(0, 0, 0, 0);
    end = new Date(start);
    end.setDate(end.getDate() + 1);
  } else if (period === 'week') {
    start = startOfWeek(date);
    end = new Date(start);
    end.setDate(end.getDate() + 7);
  } else if (period === 'year') {
    start = new Date(date.getFullYear(), 0, 1);
    end = new Date(date.getFullYear() + 1, 0, 1);
  } else {
    start = new Date(date.getFullYear(), date.getMonth(), 1);
    end = new Date(date.getFullYear(), date.getMonth() + 1, 1);
  }
  return { start, end };
};

const formatAmount = (amount) =>
  `$ ${(Number(amount) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (date) => {
  if (!date) return '';
  const parsed = new Date(date);
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Sort a {value, total} breakdown by revenue, highest first.
const sortByTotal = (entries) =>
  [...(entries || [])].sort((a, b) => b.total - a.total);

const resolveLabel = (vocabulary, value) =>
  vocabulary.find((item) => item.value === value)?.label || value || '—';

// Percentage change of current vs previous, or null when there's no
// previous value to meaningfully compare against.
const getChange = (current, previous) => {
  if (!previous) return null;
  return ((current - previous) / previous) * 100;
};

const REVENUE_SUBREQUEST = 'dashboard-revenue';
const REVENUE_PREVIOUS_SUBREQUEST = 'dashboard-revenue-previous';

const PERIOD_STORAGE_KEY = 'dashboard:period';
const DATE_STORAGE_KEY = 'dashboard:activeDate';

const readStorage = (key) => {
  try {
    return typeof window !== 'undefined'
      ? window.localStorage.getItem(key)
      : null;
  } catch (error) {
    return null;
  }
};

const writeStorage = (key, value) => {
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(key, value);
    }
  } catch (error) {
    // Ignore (e.g. private browsing mode with storage disabled).
  }
};

const initialPeriod = () => {
  const stored = readStorage(PERIOD_STORAGE_KEY);
  return periodOptions.some((option) => option.value === stored)
    ? stored
    : 'month';
};

const initialActiveDate = () => {
  const stored = readStorage(DATE_STORAGE_KEY);
  const parsed = stored ? new Date(stored) : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed : new Date();
};

/**
 * DashboardStat, a single labeled figure, with an optional change
 * indicator compared to the previous period.
 * @function DashboardStat
 * @param {Object} props Component properties.
 * @returns {string} Markup of the component.
 */
const DashboardStat = ({ label, value, change }) => (
  <div className="dashboard-stat">
    <div className="dashboard-stat-label">{label}</div>
    <div className="dashboard-stat-value">{value}</div>
    {change !== null && change !== undefined && (
      <div
        className={`dashboard-stat-change ${change >= 0 ? 'positive' : 'negative'}`}
      >
        <Icon name={change >= 0 ? upSVG : downSVG} size="14px" />
        {Math.abs(change).toFixed(1)}%
      </div>
    )}
  </div>
);

DashboardStat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  change: PropTypes.number,
};

DashboardStat.defaultProps = {
  change: null,
};

/**
 * RevenueBreakdown, a list of {value, total} entries sorted by revenue.
 * @function RevenueBreakdown
 * @param {Object} props Component properties.
 * @returns {string} Markup of the component.
 */
const RevenueBreakdown = ({ title, entries, getLabel, getLink }) => (
  <div className="dashboard-breakdown">
    <h2>{title}</h2>
    <ul>
      {sortByTotal(entries).map((entry) => {
        const content = (
          <>
            <span className="dashboard-breakdown-label">
              {getLabel(entry.value)}
            </span>
            <span className="dashboard-breakdown-value">
              {formatAmount(entry.total)}
            </span>
          </>
        );
        return (
          <li key={entry.value ?? 'none'}>
            {getLink ? (
              <Link to={getLink(entry.value)}>{content}</Link>
            ) : (
              content
            )}
          </li>
        );
      })}
      {!entries?.length && <li className="empty">No data</li>}
    </ul>
  </div>
);

RevenueBreakdown.propTypes = {
  title: PropTypes.string.isRequired,
  entries: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      total: PropTypes.number,
    }),
  ),
  getLabel: PropTypes.func.isRequired,
  getLink: PropTypes.func,
};

RevenueBreakdown.defaultProps = {
  entries: [],
  getLink: null,
};

/**
 * SizeBreakdown, a list of {label, code, quantity, total} entries, one row
 * per tool size/set sold, sorted by code (already sorted that way by the
 * backend).
 * @function SizeBreakdown
 * @param {Object} props Component properties.
 * @returns {string} Markup of the component.
 */
const SizeBreakdown = ({ title, entries }) => (
  <div className="dashboard-breakdown dashboard-breakdown-sizes">
    <h2>{title}</h2>
    <ul>
      {(entries || []).map((entry) => (
        <li key={entry.code}>
          <span className="dashboard-breakdown-label">{entry.label}</span>
          <span className="dashboard-breakdown-qty">{entry.quantity}</span>
          <span className="dashboard-breakdown-value">
            {formatAmount(entry.total)}
          </span>
        </li>
      ))}
      {!entries?.length && <li className="empty">No data</li>}
    </ul>
  </div>
);

SizeBreakdown.propTypes = {
  title: PropTypes.string.isRequired,
  entries: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      code: PropTypes.string,
      quantity: PropTypes.number,
      total: PropTypes.number,
    }),
  ),
};

SizeBreakdown.defaultProps = {
  entries: [],
};

/**
 * Dashboard view component class.
 * @function DashboardView
 * @param {Object} content Content object.
 * @returns {string} Markup of the component.
 */
const DashboardView = ({ content }) => {
  const dispatch = useDispatch();
  const { reactSelect } = useLazyLibs('reactSelect');
  const Select = reactSelect?.default;
  const [period, setPeriod] = useState(initialPeriod);
  const [activeDate, setActiveDate] = useState(initialActiveDate);

  useEffect(() => {
    writeStorage(PERIOD_STORAGE_KEY, period);
  }, [period]);

  useEffect(() => {
    writeStorage(DATE_STORAGE_KEY, activeDate.toISOString());
  }, [activeDate]);

  const onToday = () => setActiveDate(new Date());
  const onPrevious = () =>
    setActiveDate((current) => shiftDate(current, period, -1));
  const onNext = () =>
    setActiveDate((current) => shiftDate(current, period, 1));

  useEffect(() => {
    dispatch(getVocabulary({ vocabNameOrURL: 'contacts' }));
    dispatch(getVocabulary({ vocabNameOrURL: 'payment' }));
  }, [dispatch]);

  const fetchRevenue = (start, end, subrequest) =>
    dispatch(
      getContent(
        `/@revenue?start=${encodeURIComponent(start.toISOString())}&end=${encodeURIComponent(end.toISOString())}`,
        null,
        subrequest,
      ),
    );

  useEffect(() => {
    const { start, end } = getPeriodRange(activeDate, period);
    fetchRevenue(start, end, REVENUE_SUBREQUEST);

    const previousDate = shiftDate(activeDate, period, -1);
    const previousRange = getPeriodRange(previousDate, period);
    fetchRevenue(
      previousRange.start,
      previousRange.end,
      REVENUE_PREVIOUS_SUBREQUEST,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, activeDate, period]);

  const revenueData = useSelector(
    (state) => state.content.subrequests?.[REVENUE_SUBREQUEST]?.data,
  );
  const previousRevenueData = useSelector(
    (state) => state.content.subrequests?.[REVENUE_PREVIOUS_SUBREQUEST]?.data,
  );
  const revenue = revenueData?.total;

  const contactsVocabulary = useSelector(
    (state) => state.vocabularies?.contacts?.items || [],
  );
  const paymentVocabulary = useSelector(
    (state) => state.vocabularies?.payment?.items || [],
  );

  return (
    <>
      <div className="button-toolbar">
        <div className="dashboard-nav">
          <Button className="rounded" type="button" onClick={onToday}>
            Today
          </Button>
          <button
            type="button"
            className="circular-button"
            onClick={onPrevious}
            aria-label="Previous"
          >
            <Icon name={leftKeySVG} size="20px" />
          </button>
          <button
            type="button"
            className="circular-button"
            onClick={onNext}
            aria-label="Next"
          >
            <Icon name={rightKeySVG} size="20px" />
          </button>
          <span className="dashboard-period-label">
            {formatPeriodLabel(activeDate, period)}
          </span>
          {period === 'week' && (
            <Label circular>Week {getWeekNumber(activeDate)}</Label>
          )}
        </div>
        {Select && (
          <div className="dashboard-period">
            <Select
              id="dashboard-period"
              name="dashboard-period"
              isSearchable={false}
              isClearable={false}
              className="react-select-container"
              classNamePrefix="react-select"
              options={periodOptions}
              styles={customSelectStyles}
              theme={selectTheme}
              components={{ DropdownIndicator, ClearIndicator }}
              value={periodOptions.find((option) => option.value === period)}
              onChange={(selected) => setPeriod(selected.value)}
            />
          </div>
        )}
      </div>
      <div className="view-padding">
        <article id="channel">
          <h1 className="content-title">Revenue</h1>
          {content.description && (
            <p className="description">{content.description}</p>
          )}
          <div className="dashboard-stats">
            <DashboardStat
              label="Total"
              value={formatAmount(revenue)}
              change={getChange(revenue, previousRevenueData?.total)}
            />
            <DashboardStat
              label="Orders"
              value={revenueData?.count || 0}
              change={getChange(revenueData?.count, previousRevenueData?.count)}
            />
            <DashboardStat
              label="Average order"
              value={formatAmount(revenueData?.average)}
              change={getChange(
                revenueData?.average,
                previousRevenueData?.average,
              )}
            />
            <DashboardStat
              label="Without fees"
              value={formatAmount(revenueData?.subtotal)}
              change={getChange(
                revenueData?.subtotal,
                previousRevenueData?.subtotal,
              )}
            />
            <DashboardStat
              label="Fees collected"
              value={formatAmount(revenueData?.fees)}
              change={getChange(revenueData?.fees, previousRevenueData?.fees)}
            />
          </div>
          <div className="dashboard-breakdowns">
            <RevenueBreakdown
              title="Channels"
              entries={revenueData?.byChannel}
              getLabel={(value) => value || '—'}
            />
            <RevenueBreakdown
              title="Payment methods"
              entries={revenueData?.byPayment}
              getLabel={(value) => resolveLabel(paymentVocabulary, value)}
            />
          </div>
          <div className="dashboard-breakdowns">
            <RevenueBreakdown
              title="Contacts"
              entries={revenueData?.byContact}
              getLabel={(value) => resolveLabel(contactsVocabulary, value)}
              getLink={(value) => `/contacts/${value}`}
            />
          </div>
          <div className="dashboard-breakdowns">
            <SizeBreakdown title="Tools" entries={revenueData?.bySize} />
          </div>
        </article>
      </div>
    </>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
DashboardView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
};

export default DashboardView;
